import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged, sendPasswordResetEmail, updateProfile } from 'firebase/auth';
import { auth } from './firebase-config';
export class AuthService {
    constructor() {
        this.currentUser = null;
        this.authStateCallbacks = [];
        this.authInitialized = false;
        onAuthStateChanged(auth, (user) => {
            this.currentUser = user;
            this.authInitialized = true;
            this.authStateCallbacks.forEach(callback => callback(user));
        });
    }
    onAuthStateChanged(callback) {
        this.authStateCallbacks.push(callback);
        // Only call immediately if Firebase has already initialized
        if (this.authInitialized) {
            callback(this.currentUser);
        }
        return () => {
            const index = this.authStateCallbacks.indexOf(callback);
            if (index > -1) {
                this.authStateCallbacks.splice(index, 1);
            }
        };
    }
    /**
     * Get current user
     */
    getCurrentUser() {
        return this.currentUser;
    }
    /**
     * Get ID token for API calls
     */
    async getIdToken() {
        if (!this.currentUser)
            return null;
        try {
            return await this.currentUser.getIdToken();
        }
        catch (error) {
            console.error('Error getting ID token:', error);
            return null;
        }
    }
    /**
     * Sign up with email/password
     */
    async signUp(email, password, displayName) {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            // Update display name if provided
            if (displayName) {
                await updateProfile(userCredential.user, { displayName });
            }
            return userCredential.user;
        }
        catch (error) {
            throw new Error(this.getErrorMessage(error.code));
        }
    }
    /**
     * Sign in with email/password
     */
    async signIn(email, password) {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            return userCredential.user;
        }
        catch (error) {
            throw new Error(this.getErrorMessage(error.code));
        }
    }
    async signInWithGoogle() {
        try {
            const provider = new GoogleAuthProvider();
            const userCredential = await signInWithPopup(auth, provider);
            return userCredential.user;
        }
        catch (error) {
            console.error('Firebase Google Sign-In Error:', error);
            console.error('Error Code:', error.code);
            console.error('Error Message:', error.message);
            throw new Error(this.getErrorMessage(error.code));
        }
    }
    async signOut() {
        try {
            await signOut(auth);
        }
        catch (error) {
            console.error('Error signing out:', error);
            throw error;
        }
    }
    /**
     * Password reset
     */
    async resetPassword(email) {
        try {
            await sendPasswordResetEmail(auth, email);
        }
        catch (error) {
            throw new Error(this.getErrorMessage(error.code));
        }
    }
    /**
     * User-friendly error messages
     */
    getErrorMessage(code) {
        const errorMessages = {
            'auth/email-already-in-use': 'Email address is already in use',
            'auth/invalid-email': 'Invalid email address',
            'auth/operation-not-allowed': 'Google Sign-In is not enabled. Please enable it in Firebase Console: Authentication → Sign-in method → Google',
            'auth/weak-password': 'Password is too weak. Use at least 6 characters',
            'auth/user-disabled': 'User account has been disabled',
            'auth/user-not-found': 'Invalid email or password',
            'auth/wrong-password': 'Invalid email or password',
            'auth/too-many-requests': 'Too many attempts. Please try again later',
            'auth/network-request-failed': 'Network error. Please check your connection',
            'auth/popup-closed-by-user': 'Sign-in cancelled',
            'auth/cancelled-popup-request': 'Sign-in cancelled'
        };
        return errorMessages[code] || `Authentication error: ${code}`;
    }
}
// Singleton instance
export const authService = new AuthService();
